<script setup lang="ts">
import type AntiRobot from '@/components/base/form/AntiRobot.vue';

const { t } = useTexts();
const { visitorOrUserCountry } = useParameters();
const { openToaster } = useLayoutStore();
const {
  forgotPassword,
  logout,
  setRecoveryPasswordFlow,
  setRecoveryPasswordData,
} = useAuthStore();

const { openRecoveryPasswordOptions } = useAuth();
const { isCaptchaRecoverPasswordEnabled, isInvisibleCaptcha } = useCaptcha();

const isLoadingForgot = ref<boolean>(false);
const userLoginValue = ref<string>('');

const antiRobotRef = ref<InstanceType<typeof AntiRobot> | null>(null);

const antiRobotToken = computed<string>(
  () => antiRobotRef.value?.finalCaptchaTokenValue ?? '',
);

const captchaComponentClass = computed(() => {
  if (!isCaptchaRecoverPasswordEnabled.value) {
    return 'absolute opacity-0 -z-10 pointer-events-none mt-0 mb-0';
  }

  if (isInvisibleCaptcha.value) {
    return 'mt-2 mb-0 relative';
  }

  return 'mt-5 -mb-3';
});

const loginFieldLabel = computed(() => {
  return t('auth.recover_password.document_id');
});

const validateDocumentLength = computed((): boolean => {
  if (visitorOrUserCountry.value === 'BRA') {
    return userLoginValue.value.length === 11;
  }

  return userLoginValue.value.length >= 5;
});

const activeSubmitButton = computed(() => {
  if (!validateDocumentLength.value) {
    return false;
  }

  if (isCaptchaRecoverPasswordEnabled.value && !antiRobotToken.value) {
    return false;
  }

  return true;
});

const submitFormResetPassword = () => {
  if (isCaptchaRecoverPasswordEnabled.value && !antiRobotToken.value) {
    openToaster({
      message: t('auth.login.verify_captcha'),
      type: 'warning',
    });

    return;
  }

  if (!userLoginValue.value || !validateDocumentLength.value) {
    openToaster({
      message: t('account.user_data.errors.doc_invalid'),
      type: 'error',
    });

    return;
  }

  // Remove the "email" field below when the API has already been updated
  const data: IForgotPassword = {
    document: userLoginValue.value,
    captcha_token: antiRobotToken.value,
  };

  isLoadingForgot.value = true;

  setTimeout(() => {
    submitApiResetPassword(data);
  }, 200);
};

const handleDocumentInput = (event: InputEvent) => {
  const input = event.target as HTMLInputElement;

  if (visitorOrUserCountry.value === 'BRA') {
    userLoginValue.value = input.value.replace(/\D/g, '');
  }
};

const submitApiResetPassword = async (data: IForgotPassword) => {
  await logout({ displayMessage: false, redirectToHome: false });

  forgotPassword(data)
    .then((response) => {
      if (response.status.toString().startsWith('20')) {
        setRecoveryPasswordFlow(response.data);
        setRecoveryPasswordData({ login: data.document });
        openRecoveryPasswordOptions();
      } else {
        isLoadingForgot.value = false;
      }
    })
    .catch((err) => {
      console.error(err);
    })
    .finally(() => {
      isLoadingForgot.value = false;
    });
};
</script>

<template>
  <ClientOnly>
    <div class="w-full p-5 gap-4 flex flex-col">
      <form
        class="w-full max-w mx-auto flex flex-col gap-[15px]"
        @submit.prevent="submitFormResetPassword"
      >
        <BaseLoading
          class="!bg-auth-bg-primary"
          :icon-loader="true"
          :show="isLoadingForgot"
        />
        <BaseFormInput
          id="login"
          v-model="userLoginValue"
          autocomplete="off"
          :label="loginFieldLabel"
          name="login"
          tabindex="1"
          type="text"
          @input="handleDocumentInput"
        />

        <div class="relative w-full">
          <button
            :class="[
              $style.btnSubmit,
              'bg-[radial-gradient(ellipse_138.00%_168.30%_at_36.76%_-16.67%,_#FF704A_0%,_#FF3400_100%)]',
            ]"
            :disabled="!activeSubmitButton"
            tabindex="1"
            type="submit"
          >
            {{ $t('auth.login.recover_password') }}
          </button>

          <div :class="captchaComponentClass">
            <BaseFormAntiRobot ref="antiRobotRef" />
          </div>
        </div>

        <BaseModalFooter />
      </form>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.btnSubmit {
  @apply inline-flex h-12 w-full items-center justify-center gap-2.5 rounded-[10px]
         px-4 py-3 text-base font-semibold text-white transition;
  @apply bg-[radial-gradient(ellipse_138.00%_168.30%_at_36.76%_-16.67%,_#FF704A_0%,_#FF3400_100%)];

  @apply hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-white/20;
  &[disabled]:not([disabled='false']) {
    @apply cursor-not-allowed opacity-70 saturate-100 brightness-95;
  }

  svg {
    @apply text-sm;
  }
}
</style>
